<template>
  <div
    class="i-tiptap-header"
    role="toolbar"
    aria-label="Editor toolbar"
    :aria-disabled="showSource ? 'true' : 'false'"
    tabindex="-1"
    @keydown.capture="onToolbarKeydown"
    @focus="this.focusActive"
  >
    <div class="button-group" tabindex="-1">
      <!-- MAIN MENUBAR -->
      <template v-for="(btn, idx) in menubarConfig" :key="idx">
        <!-- Visual separators (not focusable) -->
        <span
          v-if="btn === '/'"
          class="new-line"
          role="separator"
          aria-hidden="true"
        ></span>
        <span
          v-else-if="btn === '-'"
          class="separator"
          role="separator"
          aria-hidden="true"
        ></span>

        <!-- Dropdowns -->
        <div
          v-else-if="btn.type === 'dropdown'"
          class="menubar-item"
          role="button"
          :aria-label="btn.label || btn.title || btn.name"
          :aria-haspopup="'menu'"
          :aria-expanded="activeDropdownIndex === idx ? 'true' : 'false'"
          :aria-disabled="isDisabled(btn) ? 'true' : 'false'"
          @click.stop.prevent="handleDropdownToggle(idx, btn)"
          @keydown.stop.prevent="onItemKeydown($event, idx, btn)"
        >
          <TiptapSelect
            :method="runEditorMethod"
            :editor="editor"
            :name="btn.name"
            :label="btn.label"
            :title="btn.title"
            :selection="btn.selection"
            :options="btn.options"
            :disabled="showSource || isDisabled(btn)"
            :filtered="btn.filtered"
            ref="menubarItemRefs"
            :data-menubar-index="idx"
            :tabindex="-1"
          />
        </div>

        <!-- Search -->
        <div
          v-else-if="btn.type === 'search'"
          class="menubar-item"
          role="button"
          :aria-label="'Search'"
          :tabindex="-1"
          :aria-disabled="isDisabled(btn) ? 'true' : 'false'"
          :data-menubar-index="idx"
          ref="menubarItemRefs"
          @keydown="onItemKeydown($event, idx, btn)"
          @click.stop.prevent="runEditorMethod('openFind')"
        >
          <TiptapSearch 
            :editor="editor" 
            :disabled="showSource || isDisabled(btn)"
            ref="menubarItemRefs"
            :data-menubar-index="idx"
            :tabindex="-1"
          />
        </div>

        <!-- Table widget trigger -->
        <div
          v-else-if="btn.type === 'table'"
          class="menubar-item"
          role="button"
          :aria-label="'Table'"
          :aria-haspopup="'menu'"
          :aria-expanded="activeDropdownIndex === idx ? 'true' : 'false'"
          :tabindex="-1"
          :aria-disabled="isDisabled(btn) ? 'true' : 'false'"
          :data-menubar-index="idx"
          ref="menubarItemRefs"
          @keydown="onItemKeydown($event, idx, btn)"
          @click.stop.prevent="handleDropdownToggle(idx, btn)"
        >
          <TiptapTable 
            :editor="editor" 
            :disabled="showSource || isDisabled(btn)" 
            ref="menubarItemRefs"
            :data-menubar-index="idx"
            :tabindex="-1"
          />
        </div>

        <!-- Regular buttons -->
        <i-button
          v-else
          class="menubar-item i-btn btn"
          role="button"
          :class="[{ active: isEditorActive(btn.name, btn.options, btn.active) }, 'i-btn btn']"
          :disabled="!canEditorMethod(btn.method, btn.options) || isDisabled(btn)"
          :title="btn.title || btn.label"
          :aria-label="btn.label || btn.title || btn.name"
          :aria-pressed="isToggle(btn) ? isEditorActive(btn.name, btn.options, btn.active) : undefined"
          :aria-disabled="(!canEditorMethod(btn.method, btn.options) || isDisabled(btn)) ? 'true' : 'false'"
          :tabindex="-1"
          :data-menubar-index="idx"
          ref="menubarItemRefs"
          @click.prevent.stop="runEditorMethod(btn.method, btn.options)"
          @keydown.stop.prevent="onItemKeydown($event, idx, btn)"
        >
          <i-icon :icon="btn.icon" type="far" size="medium" :name="btn.name"></i-icon>
          <span v-if="btn.label">{{ btn.label }}</span>
        </i-button>
      </template>

      <template v-for="(menu, mIdx) in contextMenus" :key="menu.name">
        <template v-if="shouldShowContextMenu(menu)">
          <span class="new-line hard-break" role="separator" aria-hidden="true"></span>
          <template v-for="(btn, name, tIdx) in menu.items" :key="'tbl-' + tIdx">
            <i-button
              :class="[{ active: isEditorActive(btn.name, btn.options, btn.active) }, 'menubar-item i-btn btn']"
              role="button"
              :disabled="!canEditorMethod(btn.method, btn.options) || isDisabled(btn)"
              :title="btn.title || btn.label"
              :aria-label="btn.label || btn.title || btn.name"
              :aria-pressed="isToggle(btn) ? isEditorActive(btn.name, btn.options, btn.active) : undefined"
              :aria-disabled="(!canEditorMethod(btn.method, btn.options) || isDisabled(btn)) ? 'true' : 'false'"
              :tabindex="-1"
              :data-menubar-index="menubarConfig.length + menuIndex(mIdx) + tIdx"
              ref="menubarItemRefs"
              @click.prevent.stop="runEditorMethod(btn.method, btn.options)"
              @keydown.stop.prevent="onItemKeydown($event, menubarConfig.length + menuIndex(mIdx) + tIdx, btn)"
            >
              <i-icon :icon="btn.icon" type="far" size="medium" :name="btn.name"></i-icon>
              <span v-if="btn.label">{{ btn.label }}</span>
            </i-button>
          </template>
        </template>
      </template>
    </div>

    <!-- Modals -->
    <TiptapLinkModal v-if="linkModal" v-model:show="showLinkModal" :editor="editor"/>
    <TiptapImageModal v-if="imageModal" v-model:show="showImageModal" :editor="editor"/>
    <TiptapInfoModal v-model:show="showInfoModal" :editor="editor"/>
  </div>
</template>

<script>
import Button from '@/components/lib/button/Button.vue'
import Icon from '@/components/lib/icon/Icon.vue'
import Modal from '@/components/lib/modal/Modal.vue'
import TiptapSelect from '@/components/TiptapSelect.vue'
import TiptapSearch from '@/components/TiptapSearch.vue'
import TiptapTable from '@/components/TiptapTable.vue'
import TiptapImageModal from '@/components/TiptapImageModal.vue'
import TiptapLinkModal from '@/components/TiptapLinkModal.vue'
import TiptapInfoModal from '@/components/TiptapInfoModal.vue'

import globals from '@/globals.js'

import { hasCustomImage, hasCustomLink } from '@/utils/tiptap'

export default {
  name: 'TiptapMenubar',
  components: {
    [Icon.name]: Icon,
    [Button.name]: Button,
    [Modal.name]: Modal,
    TiptapSelect,
    TiptapSearch,
    TiptapTable,
    TiptapImageModal,
    TiptapLinkModal,
    TiptapInfoModal,
  },
  emits: ['update:sourceMode', 'update:spellCheck', 'toggleImageModal', 'toggleLinkModal'],
  data() {
    return {
      showSource: false,
      showSpellCheck: true,
      searchModalOptions: { show: false, searchText: '', replaceText: '' },
      showInfoModal: false,

      // keyboard navigation state
      activeIndex: 0, // roving tabindex owner
      activeDropdownIndex: null,
    }
  },
  props: {
    editor: { type: Object, required: true },
    config: { type: Object, required: true },
    sourceMode: { type: Boolean, required: true },
    spellCheck: { type: Boolean },
    pShowImageModal: { type: Boolean, required: true },
    pShowLinkModal: { type: Boolean, required: true }
  },
  computed: {
    menubarConfig() { return this.config.menubar },
    contextMenus() { return this.config?.contextMenus },
    showImageModal: {
      get() { return this.pShowImageModal },
      set(v) { this.$emit('toggleImageModal', v) }
    },
    showLinkModal: {
      get() { return this.pShowLinkModal },
      set(v) { this.$emit('toggleLinkModal', v) }
    },
    visibleContextMenus() {
      if (!this.contextMenus) return [];
      return this.contextMenus.filter(m => m && m.show && m.show(this.editor, this.config));
    },
    activeContextMenu() {
      return this.visibleContextMenus.reduce((acc, m) => {
        return Object.assign(acc, m.items);
      }, {});
    },
    activeContextMenuLength() {
      return Object.values(this.activeContextMenu).length;
    },
    totalFocusableCount() {
      return this.menubarConfig.length + Object.values(this.activeContextMenu).length;
    },
    linkModal() {
      return hasCustomLink(this.editor);
    },
    imageModal() {
      return hasCustomImage(this.editor);
    },
  },
  mounted() {
    // Initialize activeIndex to first enabled item
    this.activeIndex = this.findNextEnabledIndex(0, 0)
    // this.$nextTick(() => this.focusActive())
  },
  watch: {
    menubarConfig: {
      handler() {
        // ensure active index always points to an enabled item
        this.activeIndex = this.findNearestEnabled(this.activeIndex)
      },
      deep: true
    },
    activeContextMenu() {
      if (this.activeIndex > this.totalFocusableCount) {
        this.activeIndex = this.totalFocusableCount - 1;
      }

      this.activeIndex = this.findNearestEnabled(this.activeIndex);
    },
  },
  methods: {
    // i18n passthrough
    __(str) { return globals.I.gt.__(str) },
    __x(str, params) { return globals.I.gt.__x(str, params) },

    // ===== Accessibility helpers =====
    isDisabled(btn) {
      // treat items as disabled when showSource is on (except toggleSource)
      if (this.showSource && btn?.method !== 'toggleSource') return true
      // consumers may provide btn.disabled boolean or function(editor)
      if (typeof btn?.disabled === 'function') return !!btn.disabled(this.editor)
      if (typeof btn?.disabled === 'boolean') return btn.disabled
      return false
    },
    isToggle(btn) {
      // mark-like toggles can expose aria-pressed
      return ['bold', 'italic', 'strike', 'underline', 'bulletList', 'orderedList'].includes(btn?.name)
    },

    focusActive() {
      let el = this.$refs.menubarItemRefs?.find?.(n => Number(n?.button?.dataset?.menubarIndex || n?.$el?.dataset?.menubarIndex || n?.dataset?.menubarIndex) === this.activeIndex)
      if (!el) {
        return;
      }
      
      el = el.button || el;
      if (el && typeof el.focus === 'function') el.focus({ preventScroll: true })
    },

    findNextEnabledIndex(start, step) {
      if (this.totalFocusableCount === 0) return 0
      let i = start
      for (let tries = 0; tries < this.totalFocusableCount; tries++) {
        i = (i + this.totalFocusableCount + step) % this.totalFocusableCount
        const btn = this.getButtonByFlatIndex(i)
        if (!this.isDisabled(btn) && this.isFocusableType(btn)) return i
      }
      return start // fallback
    },
    findPrevEnabledIndex(start) { return this.findNextEnabledIndex(start, -1) },
    findNearestEnabled(i) {
      if (!this.isDisabled(this.getButtonByFlatIndex(i))) return i
      const next = this.findNextEnabledIndex(i, +1)
      const prev = this.findNextEnabledIndex(i, -1)
      // choose the closer one
      const dNext = (next - i + this.totalFocusableCount) % this.totalFocusableCount
      const dPrev = (i - prev + this.totalFocusableCount) % this.totalFocusableCount
      return dPrev <= dNext ? prev : next
    },

    isFocusableType(btn) {
      if (!btn) return false
      if (btn === '/' || btn === '-') return false
      return true
    },

    getButtonByFlatIndex(idx) {
      if (idx < this.menubarConfig.length) return this.menubarConfig[idx]
      const tIdx = idx - this.menubarConfig.length
      return Object.values(this.activeContextMenu)[tIdx];
    },

    // ===== Event handlers =====
    onToolbarKeydown(e) {
      if ((e.key === 'Escape') && this.activeDropdownIndex === null) {
        this.editor.commands.focus()
      }

      // If focus is outside toolbar items (e.g., clicked editor), pressing Alt+F10 focuses toolbar
      // This is just a formality as it will not work on some browsers or different os.
      if ((e.altKey && e.key.toLowerCase() === 'f10')) {
        e.preventDefault()
        this.focusActive()
      }
    },

    onItemKeydown(e, idx, btn) {
      if (this.isDisabled(btn)) return
      const key = e.key

      if (key === 'ArrowRight') {
        this.activeIndex = this.findNextEnabledIndex(idx, +1)
        this.$nextTick(this.focusActive)
        return
      }
      if (key === 'ArrowLeft') {
        this.activeIndex = this.findNextEnabledIndex(idx, -1)
        this.$nextTick(this.focusActive)
        return
      }
      if (key === 'Home') {
        this.activeIndex = this.findNearestEnabled(0)
        this.$nextTick(this.focusActive)
        return
      }
      if (key === 'End') {
        this.activeIndex = this.findNearestEnabled(this.totalFocusableCount - 1)
        this.$nextTick(this.focusActive)
        return
      }
      // Open dropdown-like items with ArrowDown/Enter/Space
      const isDropdownish = btn?.type;
      if (isDropdownish && this.activeDropdownIndex === null && (key === 'ArrowDown' || key === 'Enter' || key === ' ')) {
        this.handleDropdownOpen(idx)
        return
      }

      // Activate buttons with Enter/Space
      if (!isDropdownish && (key === 'Enter' || key === ' ')) {
        this.runEditorMethod(btn.method, btn.options)
        return
      }

      // Escape closes any open dropdown
      if (key === 'Escape' && this.activeDropdownIndex !== null) {
        this.handleDropdownClose()
        return
      }
    },

    handleDropdownToggle(idx, btn) {
      if (this.activeDropdownIndex === idx) this.handleDropdownClose()
      else this.handleDropdownOpen(idx, btn)
    },
    handleDropdownOpen(idx) {
      this.activeDropdownIndex = idx
    },
    handleDropdownClose() {
      this.activeIndex = this.activeDropdownIndex;
      this.activeDropdownIndex = null
      this.$nextTick(this.focusActive)
    },

    // ===== Existing methods (unchanged) =====
    openImageModal() { this.showImageModal = true },
    openLinkModal() { this.showLinkModal = true },
    openFind() { this.searchModalOptions.show = true },
    openReplace() { this.searchModalOptions.show = true },
    removeLink() { this.editor.chain().focus().extendMarkRange('link').unsetLink().run() },
    isEditorActive(name, options = '', active) {
      if (active && typeof active === 'function') return active(this.editor)
      if (options) return this.editor.isActive({ [name]: options })
      return this.editor.isActive(name)
    },
    runEditorMethod(method, options = {}) {
      if (this.editor && this.editor.can?.()[method]) {
        this.editor.chain().focus?.()[method](options).run?.()
      } else if (typeof this[method] === 'function') {
        this[method](options)
      } else if (typeof method === 'function') {
        method(this.editor, options)
      } else {
        console.error(this.__x('IWE3 menubar {id}: Editor instance is not properly initialized. Invalid method: {method}', {method: method, id: this.config.id}))
      }
    },
    canEditorMethod(method, options = '') {
      if (this.showSource && method !== 'toggleSource') return false
      if (this.editor && this.editor.can?.()[method]) return true
      if (typeof this[method] === 'function') return true
      console.error(this.__x('IWE3 menubar {id}: Editor instance is not properly initialized. Invalid method: {method}', {method: method, id: this.config.id}))
      return false
    },

    copy() { document.execCommand('copy') },
    icut() { document.execCommand('cut') },
    toggleSource() {
      this.showSource = !this.showSource
      this.$emit('update:sourceMode', this.showSource)
      // When toggled, ensure focus stays within toolbar
      this.$nextTick(() => {
        this.activeIndex = this.findNearestEnabled(this.activeIndex)
        this.focusActive()
      })
    },
    clearAll() { this.editor.chain().setContent('').run() },
    print() {
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        console.error(this.__x('IWE3 menubar {id}: Failed to open print window', {id: this.config.id}))
        return false
      }
      const content = this.editor.getHTML()
      const page_title = this.__('Print')
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${page_title}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
            </style>
          </head>
          <body>${content}</body>
        </html>
      `)
      printWindow.document.close()
      printWindow.onafterprint = () => { printWindow.close() }
      printWindow.print()
      return true
    },
    toggleSpellCheck() {
      this.showSpellCheck = !this.showSpellCheck
      this.$emit('update:spellCheck', this.showSpellCheck)
    },
    selectAll() { this.editor.chain().focus().selectAll().run() },
    removeFormat() { this.editor.chain().focus().unsetAllMarks().run() },
    setCustomStyle(options) {
      const { element, attrs } = options
      const isNode = this.editor.schema.nodes[element] !== undefined
      const isMark = this.editor.schema.marks[element] !== undefined
      const isWrap = isNode && !this.editor.schema.nodes[element].inlineContent
      if (isWrap) this.editor.chain().focus().toggleWrap().toggleWrap(element, attrs).run()
      else if (isNode) this.editor.chain().focus().setNode(element, attrs).run()
      else if (isMark) this.editor.chain().focus().setMark(element, attrs).run()
      else console.warn(`IWE3 menubar ${this.config.id}: Invalid custom style element '${element}'!`)
    },
    showInfo() { this.showInfoModal = true },
    shouldShowContextMenu(menu) {
      if (!menu || !menu.show) {
        return false;
      }

      const show = this.contextMenus && menu.show(this.editor, this.config);
      return show;
    },
    menuIndex(mIdx) {
      let offset = 0;

      for (let i = 0; i < mIdx; i ++) {
        if (!this.contextMenus[i].show(this.editor, this.config)) continue;
        offset += Object.keys(this.contextMenus[i].items).length;
      }

      return offset;
    }
  },
}
</script>

<style lang="scss" scoped>
.tiptap-select-label { margin-right: 15px; }

.i-dropdown-menu {
  display: block;
  overflow-y: auto;
  max-height: 200px;
  width: 120px;
  .pl2 { width: 100%; padding-left: 8px; background-color: lightgray; border-bottom: 1px solid gray; }
}
.separator {
  display: flex;
  justify-content: center;
  align-items: center;
  border-left: 1px solid #bcbcbc;
  height: 18px;
  align-self: center;
}
.new-line { display: block; width: 100%; }
.new-line.hard-break { border-top: 1px solid var(--gray); }

/* Optional, visible focus ring for keyboard users */
.menubar-item:focus { outline: 2px solid var(--primary, #2684ff); outline-offset: 2px; border-radius: 6px; }
</style>
